import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALES, LOCALE_LABELS, type Locale } from '@arriba.digital/contracts';
import { intlLocale } from '@/shared/lib/format';
import { changeLocale, currentLocale } from './index';

/**
 * The active locale plus a setter, and a `t` that already knows the locale.
 *
 * `t` is re-exported so components never have to pass the language to
 * formatters: `const { t, locale, formatNumber } = useLocale()`.
 */
export function useLocale() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) ?? currentLocale();

  const setLocale = useCallback(async (next: Locale) => {
    await changeLocale(next);
  }, []);

  const formatNumber = useCallback(
    (value: number | string, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(intlLocale(locale), options).format(
        typeof value === 'string' ? Number(value) : value,
      ),
    [locale],
  );

  return {
    t,
    locale,
    setLocale,
    formatNumber,
    /**
     * Ready-made options for a language switcher, in `LOCALES` order
     * (Spanish, English, Russian) so every picker agrees with every other.
     */
    options: LOCALES.map((code) => ({
      value: code,
      label: LOCALE_LABELS[code],
    })),
  };
}
